/**
 * Starter templates — pre-populated block combinations the user starts from
 * when creating a new page. They're just default block arrays + an optional
 * theme override — once created, the user freely edits like any other page.
 *
 * Shipping (in order of how they appear in the picker):
 *   - blank          — empty canvas
 *   - creator-profile — light-theme all-purpose bio
 *   - pro-creator    — premium dark/violet with hero image + glass buttons
 *                      (matches DESIGN.md "Social link.me Pro" aesthetic)
 *   - one-big-cta    — single hero offer / product launch
 *   - influencer     — urgency-heavy bio (stock pill + live status + glass CTAs)
 *   - ig-creator     — Instagram-bio header + hero + glass buttons
 *   - coach          — IG-bio + booking + program + free guide (light theme)
 *   - shop           — multi-product carousel + buy buttons
 *
 * Each template can optionally provide `previewBlocks` — a variant of
 * the block array used ONLY for the picker's mobile-frame preview. We
 * inject sample images + copy so the preview thumbnails look populated,
 * while `buildBlocks` (used at create time) keeps placeholders so the
 * customer's actual new page starts clean.
 */

import type { Block, ThemeConfig } from './types';
import { DEFAULT_THEME } from './theme';

export interface StarterTemplate {
  id: string;
  name: string;
  description: string;
  /** Small emoji used as the picker tile icon (when not using preview). */
  icon: string;
  /** Block array materialized on create — usually with empty image src
   *  and placeholder copy. */
  buildBlocks: (mkId: () => string) => Block[];
  /** Optional sample-populated block array used for the picker preview
   *  thumbnail. Falls back to buildBlocks if not provided. */
  previewBlocks?: (mkId: () => string) => Block[];
  /** Optional theme overrides. Falls back to DEFAULT_THEME. */
  theme?: Partial<ThemeConfig>;
  /** Optional theme overrides for the preview thumbnail only (e.g. preview
   *  uses a sample hero image so customers see what a populated version
   *  looks like). */
  previewTheme?: Partial<ThemeConfig>;
}

// Sample images used for the picker previews. These are SVG/JPEG URLs
// that don't make real fetches at build time but produce a populated
// thumbnail in the iframe. We keep them on Unsplash because they're
// stable, free, and visually credible for creator-bio mockups.
const SAMPLE_HERO = 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=900&q=80&auto=format';
const SAMPLE_AVATAR = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80&auto=format';
const SAMPLE_PRODUCT_1 = 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&q=80&auto=format';
const SAMPLE_PRODUCT_2 = 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80&auto=format';
const SAMPLE_PRODUCT_3 = 'https://images.unsplash.com/photo-1542293787938-c9e299b88010?w=600&q=80&auto=format';
const SAMPLE_HERO_LAUNCH = 'https://images.unsplash.com/photo-1535025183041-0991a977e25b?w=900&q=80&auto=format';
// Editorial / Sultry / Music drop use these. Picked for vibe at thumbnail
// size — not for content. All Unsplash, royalty-free.
const SAMPLE_EDITORIAL_PORTRAIT = 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=900&q=80&auto=format';
const SAMPLE_SULTRY_HERO = 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=900&q=80&auto=format';
const SAMPLE_ALBUM_ART = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=900&q=80&auto=format';

export const STARTER_TEMPLATES: StarterTemplate[] = [
  // -------------------------------------------------------------------
  // Blank
  // -------------------------------------------------------------------
  {
    id: 'blank',
    name: 'Blank canvas',
    description: 'Start from nothing and build whatever you want.',
    icon: '✨',
    buildBlocks: () => [],
    previewBlocks: (mkId) => [
      { id: mkId(), type: 'paragraph', props: { text: '✨', align: 'center' } },
      { id: mkId(), type: 'paragraph', props: { text: 'Empty page — your canvas.', align: 'center' } },
    ],
  },

  // -------------------------------------------------------------------
  // Creator profile — light, all-purpose
  // -------------------------------------------------------------------
  {
    id: 'creator-profile',
    name: 'Creator profile',
    description: 'Light, all-purpose bio with avatar + buttons + socials.',
    icon: '👤',
    buildBlocks: (mkId) => [
      // Avatar uses a sample photo so the page LOOKS populated when the
      // customer lands in the editor — they replace it with their own.
      // Same for any image-bearing blocks below. Visual parity with the
      // picker preview means no "I picked X, got Y" disconnect.
      { id: mkId(), type: 'avatar', props: { src: SAMPLE_AVATAR, initials: 'Y', size: 'md' } },
      { id: mkId(), type: 'heading', props: { text: 'Your Name', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: 'Creator. Builder. Drop a tagline here.', align: 'center' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Latest video', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'My shop', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Newsletter', url: 'https://', variant: 'outline', newTab: true },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'tiktok', url: 'https://tiktok.com/@' },
            { platform: 'youtube', url: 'https://youtube.com/@' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      { id: mkId(), type: 'avatar', props: { src: SAMPLE_AVATAR, initials: 'A', size: 'md' } },
      { id: mkId(), type: 'heading', props: { text: 'Ava Chen', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: 'Designer, builder, lives in Brooklyn.', align: 'center' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Latest video', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'My shop', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Newsletter', url: 'https://', variant: 'outline', newTab: true },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'tiktok', url: 'https://tiktok.com/@' },
            { platform: 'youtube', url: 'https://youtube.com/@' },
          ],
        },
      },
    ],
  },

  // -------------------------------------------------------------------
  // Influencer — urgency-heavy bio (stock pill + countdown + glass CTAs)
  // -------------------------------------------------------------------
  {
    id: 'influencer',
    name: 'Influencer',
    description: 'Conversion-driven bio with live urgency timer + spots-left pill.',
    icon: '🔥',
    theme: {
      primaryColor: 'ec4899',
      backgroundColor: '0a0a0a',
      textColor: 'fafafa',
      fontFamily: 'sans',
      radius: 'pill',
      backgroundOverlay: true,
      // Sample hero shipped so the page looks complete at create-time.
      backgroundImage: SAMPLE_HERO_LAUNCH,
    },
    buildBlocks: (mkId) => [
      { id: mkId(), type: 'heading', props: { text: 'Your Name', level: 1, align: 'center' } },
      { id: mkId(), type: 'paragraph', props: { text: 'Creator · Coach · Sender of links', align: 'center' } },
      { id: mkId(), type: 'live-status', props: { label: 'LIVE NOW', variant: 'live', align: 'center' } },
      {
        id: mkId(),
        type: 'stock-pill',
        props: {
          startCount: 24,
          endCount: 3,
          durationSeconds: 30 * 60,
          template: '🔥 Only {n} spots left at launch price',
          variant: 'danger',
          align: 'center',
        },
      },
      {
        id: mkId(),
        type: 'countdown',
        props: {
          mode: 'duration',
          label: 'Offer ends in',
          targetIso: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
          durationSeconds: 30 * 60,
          expiredText: 'Sale ended — DM me for next drop',
          align: 'center',
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Grab the deal', url: 'https://', variant: 'glass', newTab: true, icon: '🔥', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'My content', url: 'https://', variant: 'glass', newTab: true, icon: '🥵', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'DMs (paid)', url: 'https://', variant: 'glass', newTab: true, icon: '✉️', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'tiktok', url: 'https://tiktok.com/@' },
            { platform: 'x', url: 'https://x.com/' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      { id: mkId(), type: 'heading', props: { text: 'Maya Lopez', level: 1, align: 'center' } },
      { id: mkId(), type: 'paragraph', props: { text: 'Sale today only.', align: 'center' } },
      { id: mkId(), type: 'live-status', props: { label: 'LIVE NOW', variant: 'live', align: 'center' } },
      {
        id: mkId(),
        type: 'stock-pill',
        props: {
          startCount: 17,
          endCount: 3,
          durationSeconds: 30 * 60,
          template: '🔥 Only {n} spots left',
          variant: 'danger',
          align: 'center',
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Grab the deal', url: 'https://', variant: 'glass', newTab: true, icon: '🔥', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'My content', url: 'https://', variant: 'glass', newTab: true, icon: '🥵', iconBg: 'secondary' },
      },
    ],
  },

  // -------------------------------------------------------------------
  // IG Creator — Instagram-bio header + hero photo + glass buttons.
  // The most direct "I want my page to feel like Instagram" template.
  // -------------------------------------------------------------------
  {
    id: 'ig-creator',
    name: 'IG Creator',
    description: 'Instagram-style profile header with stats, glass buttons, hero photo.',
    icon: '📸',
    theme: {
      primaryColor: '7c4dff',
      backgroundColor: '0a0a0a',
      textColor: 'fafafa',
      fontFamily: 'sans',
      radius: 'pill',
      backgroundOverlay: true,
      backgroundImage: SAMPLE_HERO,
    },
    buildBlocks: (mkId) => [
      {
        id: mkId(),
        type: 'instagram-bio',
        props: {
          avatar: SAMPLE_AVATAR,
          avatarInitials: 'Y',
          avatarSize: 'md',
          avatarCropX: 50,
          avatarCropY: 50,
          storyRing: true,
          username: 'yourhandle',
          verified: true,
          displayName: 'Your Name',
          posts: '124',
          followers: '12.4K',
          following: '386',
          category: 'Creator',
          bio: 'One line about you. Drop a tagline here.',
        },
      },
      // Spacer sits BELOW the IG bio so the hero photo behind the page
      // peeks through between the bio header and the button stack.
      // Putting it at the top (the old layout) hid the bio behind the
      // hero crop and made the page read upside-down.
      { id: mkId(), type: 'spacer', props: { height: 200 } },
      { id: mkId(), type: 'live-status', props: { label: 'Online now', variant: 'online', align: 'center' } },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Latest drop', url: 'https://', variant: 'glass', newTab: true, icon: '🔥', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Watch on YouTube', url: 'https://', variant: 'glass', newTab: true, icon: '📺', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'My shop', url: 'https://', variant: 'glass', newTab: true, icon: '🛍️', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Newsletter', url: 'https://', variant: 'glass', newTab: true, icon: '✉️', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'tiktok', url: 'https://tiktok.com/@' },
            { platform: 'youtube', url: 'https://youtube.com/@' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      {
        id: mkId(),
        type: 'instagram-bio',
        props: {
          avatar: SAMPLE_AVATAR,
          avatarInitials: 'A',
          avatarSize: 'md',
          storyRing: true,
          username: 'avadesigns',
          verified: true,
          displayName: 'Ava Chen',
          posts: '297',
          followers: '24.8K',
          following: '612',
          category: 'Designer',
          bio: 'Designer, builder, lives in Brooklyn.',
        },
      },
      { id: mkId(), type: 'spacer', props: { height: 200 } },
      { id: mkId(), type: 'live-status', props: { label: 'Online now', variant: 'online', align: 'center' } },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Latest drop', url: 'https://', variant: 'glass', newTab: true, icon: '🔥', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Watch on YouTube', url: 'https://', variant: 'glass', newTab: true, icon: '📺', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'My shop', url: 'https://', variant: 'glass', newTab: true, icon: '🛍️', iconBg: 'secondary' },
      },
    ],
  },

  // -------------------------------------------------------------------
  // Coach — IG-bio header + booking + program + free guide.
  // Light, professional vibe for coaches / consultants / speakers.
  // -------------------------------------------------------------------
  {
    id: 'coach',
    name: 'Coach',
    description: 'IG bio + booking + free guide + program — for coaches & consultants.',
    icon: '🎯',
    theme: {
      primaryColor: '4f46e5',
      backgroundColor: 'ffffff',
      textColor: '0f172a',
      fontFamily: 'sans',
      radius: 'soft',
    },
    buildBlocks: (mkId) => [
      {
        id: mkId(),
        type: 'instagram-bio',
        props: {
          avatar: SAMPLE_AVATAR,
          avatarInitials: 'Y',
          avatarSize: 'md',
          avatarCropX: 50,
          avatarCropY: 50,
          storyRing: false,
          username: 'yourhandle',
          verified: true,
          displayName: 'Your Name',
          posts: '247',
          followers: '34.8K',
          following: '512',
          category: 'Coach · Speaker',
          bio: 'I help [your audience] [achieve their outcome].\n1:1 + group programs.',
        },
      },
      {
        id: mkId(),
        type: 'live-status',
        props: { label: 'Booking new clients', variant: 'online', align: 'center' },
      },
      { id: mkId(), type: 'spacer', props: { height: 16 } },
      {
        id: mkId(),
        type: 'link-button',
        props: {
          label: 'Book a free intro call',
          url: 'https://',
          variant: 'solid',
          newTab: true,
          icon: '📞',
          iconBg: 'primary',
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: {
          label: 'Free guide: [your lead magnet]',
          url: 'https://',
          variant: 'outline',
          newTab: true,
          icon: '📘',
          iconBg: 'transparent',
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: {
          label: 'Group coaching program',
          url: 'https://',
          variant: 'outline',
          newTab: true,
          icon: '🎓',
          iconBg: 'transparent',
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: {
          label: 'Client wins / testimonials',
          url: 'https://',
          variant: 'outline',
          newTab: true,
          icon: '⭐',
          iconBg: 'transparent',
        },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'linkedin', url: 'https://linkedin.com/in/' },
            { platform: 'youtube', url: 'https://youtube.com/@' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      {
        id: mkId(),
        type: 'instagram-bio',
        props: {
          avatar: SAMPLE_AVATAR,
          avatarInitials: 'S',
          avatarSize: 'md',
          storyRing: false,
          username: 'sarahcoaches',
          verified: true,
          displayName: 'Sarah Davis',
          posts: '412',
          followers: '89.2K',
          following: '803',
          category: 'Coach · Speaker',
          bio: 'I help ambitious creators ship the thing.\n1:1 + group programs.',
        },
      },
      {
        id: mkId(),
        type: 'live-status',
        props: { label: 'Booking new clients', variant: 'online', align: 'center' },
      },
      { id: mkId(), type: 'spacer', props: { height: 16 } },
      {
        id: mkId(),
        type: 'link-button',
        props: {
          label: 'Book a free intro call',
          url: 'https://',
          variant: 'solid',
          newTab: true,
          icon: '📞',
          iconBg: 'primary',
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: {
          label: 'Free guide: Ship in 30 days',
          url: 'https://',
          variant: 'outline',
          newTab: true,
          icon: '📘',
          iconBg: 'transparent',
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: {
          label: 'Group coaching program',
          url: 'https://',
          variant: 'outline',
          newTab: true,
          icon: '🎓',
          iconBg: 'transparent',
        },
      },
    ],
  },

  // -------------------------------------------------------------------
  // Shop — multi-product carousel + buy buttons
  // -------------------------------------------------------------------
  {
    id: 'shop',
    name: 'Shop',
    description: 'Swipeable product carousel + support links for sellers.',
    icon: '🛍️',
    theme: {
      primaryColor: '16a34a',
      backgroundColor: 'fafafa',
      textColor: '0f172a',
      fontFamily: 'sans',
      radius: 'soft',
    },
    buildBlocks: (mkId) => [
      { id: mkId(), type: 'heading', props: { text: 'Shop', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: 'Swipe to see what just dropped.', align: 'center' },
      },
      {
        id: mkId(),
        type: 'carousel',
        props: {
          aspectRatio: '1/1',
          shape: 'rounded',
          showCounter: true,
          // Sample products shipped so the page LOOKS like a shop on
          // first open. Customer replaces each slide with their own.
          slides: [
            { src: SAMPLE_PRODUCT_1, alt: 'Product 1', caption: 'Featured drop', url: 'https://' },
            { src: SAMPLE_PRODUCT_2, alt: 'Product 2', caption: 'New this week', url: 'https://' },
            { src: SAMPLE_PRODUCT_3, alt: 'Product 3', caption: 'Best seller', url: 'https://' },
          ],
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Shop everything', url: 'https://', variant: 'solid', newTab: true, icon: '🛍️', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Track an order', url: 'https://', variant: 'outline', newTab: true, icon: '📦', iconBg: 'transparent' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Customer support', url: 'https://', variant: 'outline', newTab: true, icon: '💬', iconBg: 'transparent' },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'tiktok', url: 'https://tiktok.com/@' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      { id: mkId(), type: 'heading', props: { text: 'Bloom & Co', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: 'Swipe to see what just dropped.', align: 'center' },
      },
      {
        id: mkId(),
        type: 'carousel',
        props: {
          aspectRatio: '1/1',
          shape: 'rounded',
          showCounter: true,
          slides: [
            { src: SAMPLE_PRODUCT_1, alt: '', caption: 'Featured drop', url: 'https://' },
            { src: SAMPLE_PRODUCT_2, alt: '', caption: 'New this week', url: 'https://' },
            { src: SAMPLE_PRODUCT_3, alt: '', caption: 'Best seller', url: 'https://' },
          ],
        },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Shop everything', url: 'https://', variant: 'solid', newTab: true, icon: '🛍️', iconBg: 'primary' },
      },
    ],
  },

  // -------------------------------------------------------------------
  // Editorial — magazine-style serif, cream paper + oxblood accent.
  // Best for photographers, writers, fashion creators who want their
  // page to read like a print spread. No glass, no glow — just type
  // and a portrait.
  // -------------------------------------------------------------------
  {
    id: 'editorial',
    name: 'Editorial',
    description: 'Magazine-style serif — cream paper, oxblood accent, sharp corners.',
    icon: '📰',
    theme: {
      primaryColor: '8b1e3f', // oxblood
      backgroundColor: 'f5f0e8', // warm cream
      textColor: '1a1a1a', // ink
      fontFamily: 'serif',
      radius: 'sharp',
    },
    buildBlocks: (mkId) => [
      { id: mkId(), type: 'avatar', props: { src: SAMPLE_EDITORIAL_PORTRAIT, initials: 'Y', size: 'md' } },
      { id: mkId(), type: 'heading', props: { text: 'Your Name', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: 'A line about your work — keep it editorial.', align: 'center' },
      },
      { id: mkId(), type: 'divider', props: { variant: 'line' } },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Portfolio', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'In the press', url: 'https://', variant: 'outline', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Newsletter', url: 'https://', variant: 'outline', newTab: true },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'x', url: 'https://x.com/' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      { id: mkId(), type: 'avatar', props: { src: SAMPLE_EDITORIAL_PORTRAIT, initials: 'E', size: 'md' } },
      { id: mkId(), type: 'heading', props: { text: 'Emma Hart', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: 'Photographer — Paris × Berlin × Tokyo.', align: 'center' },
      },
      { id: mkId(), type: 'divider', props: { variant: 'line' } },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Portfolio', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'In the press', url: 'https://', variant: 'outline', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Newsletter', url: 'https://', variant: 'outline', newTab: true },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'x', url: 'https://x.com/' },
          ],
        },
      },
    ],
  },

  // -------------------------------------------------------------------
  // Sultry — warm dark with rose-gold accents. Serif type for elegance,
  // glass buttons over a candle-lit hero photo. The audience the
  // existing "Influencer" template (pink/black urgency) doesn't actually
  // serve aesthetically — this one's about warmth + intimacy, not
  // countdown pressure.
  // -------------------------------------------------------------------
  {
    id: 'sultry',
    name: 'Sultry',
    description: 'Warm dark with rose-gold accents — serif type, glass over candlelight.',
    icon: '🕯️',
    theme: {
      primaryColor: 'e0a87c', // rose gold
      backgroundColor: '1a0e08', // deep espresso
      textColor: 'f3e5d0', // warm cream
      fontFamily: 'serif',
      radius: 'pill',
      backgroundOverlay: true,
      backgroundImage: SAMPLE_SULTRY_HERO,
    },
    buildBlocks: (mkId) => [
      { id: mkId(), type: 'spacer', props: { height: 160 } },
      { id: mkId(), type: 'heading', props: { text: 'Your Name', level: 1, align: 'center' } },
      { id: mkId(), type: 'paragraph', props: { text: 'VIP only · 21+ content', align: 'center' } },
      { id: mkId(), type: 'live-status', props: { label: 'Online now', variant: 'online', align: 'center' } },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Subscribe — 50% off this week', url: 'https://', variant: 'glass', newTab: true, icon: '💋', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Custom requests', url: 'https://', variant: 'glass', newTab: true, icon: '✉️', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Free preview', url: 'https://', variant: 'glass', newTab: true, icon: '🎁', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'DMs (paid)', url: 'https://', variant: 'glass', newTab: true, icon: '💌', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'tiktok', url: 'https://tiktok.com/@' },
            { platform: 'x', url: 'https://x.com/' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      { id: mkId(), type: 'spacer', props: { height: 140 } },
      { id: mkId(), type: 'heading', props: { text: 'Cherry', level: 1, align: 'center' } },
      { id: mkId(), type: 'paragraph', props: { text: 'VIP only · 21+ content', align: 'center' } },
      { id: mkId(), type: 'live-status', props: { label: 'Online now', variant: 'online', align: 'center' } },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Subscribe — 50% off this week', url: 'https://', variant: 'glass', newTab: true, icon: '💋', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Custom requests', url: 'https://', variant: 'glass', newTab: true, icon: '✉️', iconBg: 'secondary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Free preview', url: 'https://', variant: 'glass', newTab: true, icon: '🎁', iconBg: 'secondary' },
      },
    ],
  },

  // -------------------------------------------------------------------
  // Music drop — album-art forward, streaming-service buttons stack.
  // For musicians, DJs, producers releasing a single/EP. Linkfire-style
  // layout: big square art at top, then "Listen on Spotify / Apple
  // Music / YouTube Music" buttons. Hot pink CTA on deep purple bg.
  // -------------------------------------------------------------------
  {
    id: 'music-drop',
    name: 'Music drop',
    description: 'Album art + streaming buttons — Spotify, Apple Music, YouTube.',
    icon: '🎵',
    theme: {
      primaryColor: 'f72585', // streaming-pink
      backgroundColor: '1e0a3a', // deep purple
      textColor: 'ffffff',
      fontFamily: 'sans',
      radius: 'soft',
    },
    buildBlocks: (mkId) => [
      { id: mkId(), type: 'heading', props: { text: 'OUT NOW', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: 'Your single / EP — one line of context.', align: 'center' },
      },
      {
        id: mkId(),
        type: 'image',
        props: { src: SAMPLE_ALBUM_ART, alt: 'Album art', shape: 'rounded', widthPct: 100, aspectRatio: '1/1', align: 'center' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Listen on Spotify', url: 'https://', variant: 'solid', newTab: true, icon: '🎧', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Apple Music', url: 'https://', variant: 'outline', newTab: true, icon: '🍎', iconBg: 'transparent' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'YouTube Music', url: 'https://', variant: 'outline', newTab: true, icon: '▶️', iconBg: 'transparent' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Pre-save next single', url: 'https://', variant: 'outline', newTab: true, icon: '⭐', iconBg: 'transparent' },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'tiktok', url: 'https://tiktok.com/@' },
            { platform: 'youtube', url: 'https://youtube.com/@' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      { id: mkId(), type: 'heading', props: { text: 'OUT NOW', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: 'Reverie · the new EP', align: 'center' },
      },
      {
        id: mkId(),
        type: 'image',
        props: { src: SAMPLE_ALBUM_ART, alt: 'Album art', shape: 'rounded', widthPct: 100, aspectRatio: '1/1', align: 'center' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Listen on Spotify', url: 'https://', variant: 'solid', newTab: true, icon: '🎧', iconBg: 'primary' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'Apple Music', url: 'https://', variant: 'outline', newTab: true, icon: '🍎', iconBg: 'transparent' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: 'YouTube Music', url: 'https://', variant: 'outline', newTab: true, icon: '▶️', iconBg: 'transparent' },
      },
    ],
  },

  // -------------------------------------------------------------------
  // Y2K — bubblegum magenta + electric cyan + sparkles. Internet-
  // princess aesthetic. Pure color contrast carries the visual — no
  // hero image, just a high-saturation background and emoji-decorated
  // copy. None of the other templates have this energy and Gen-Z
  // creators on TikTok specifically search for it.
  // -------------------------------------------------------------------
  {
    id: 'y2k',
    name: 'Y2K',
    description: 'Bubblegum magenta + electric cyan — sparkles, chrome energy.',
    icon: '✨',
    theme: {
      primaryColor: '00e0ff', // electric cyan
      backgroundColor: 'ff5cad', // hot magenta
      textColor: 'ffffff',
      fontFamily: 'sans',
      radius: 'pill',
    },
    buildBlocks: (mkId) => [
      { id: mkId(), type: 'heading', props: { text: '✨ your handle ✨', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: '💗 internet princess · drop a tagline 💗', align: 'center' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: '✨ Latest drop ✨', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: '👀 Watch my reel', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: '💌 Yapping newsletter', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: '📦 Store', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'social-row',
        props: {
          align: 'center',
          items: [
            { platform: 'instagram', url: 'https://instagram.com/' },
            { platform: 'tiktok', url: 'https://tiktok.com/@' },
          ],
        },
      },
    ],
    previewBlocks: (mkId) => [
      { id: mkId(), type: 'heading', props: { text: '✨ candy ✨', level: 1, align: 'center' } },
      {
        id: mkId(),
        type: 'paragraph',
        props: { text: '💗 multidisciplinary internet princess 💗', align: 'center' },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: '✨ Latest drop ✨', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: '👀 Watch my reel', url: 'https://', variant: 'solid', newTab: true },
      },
      {
        id: mkId(),
        type: 'link-button',
        props: { label: '💌 Yapping newsletter', url: 'https://', variant: 'solid', newTab: true },
      },
    ],
  },
];

export function getTemplate(id: string): StarterTemplate | null {
  return STARTER_TEMPLATES.find((t) => t.id === id) ?? null;
}

export { DEFAULT_THEME };
